import { Button, Divider, Stack, Typography } from '@mui/material';
import { type InventoryResource, BackupStatus } from '@repo/api-gw-sdk';
import { Fragment } from 'react';

import { BackupStatusIcon } from '@/components/backupStatus/backupStatusIcon';
import { AuthorizationTooltip } from '@/components/tooltips/authorizationTooltip';
import {
  InventoryResourceActionsLevels,
  type ResourceAction,
} from '@/data/inventory/actions';
import { BackupStatuses } from '@/data/inventory/data';

import { StatusCardHeader } from './statusCardHeader';
import { StatusCardWrapper } from './statusCardWrapper';
import { ViolationsDetails } from './violationsDetails';

interface BackupStatusCardProps {
  backupStatus: BackupStatus;
  actions: Partial<Record<InventoryResourceActionsLevels, ResourceAction[]>>;
  entity: InventoryResource;
  onEntityChange?: () => Promise<void>;
}

export function BackupStatusCard(props: BackupStatusCardProps) {
  const { backupStatus, actions, entity, onEntityChange } = props;
  const { iconColor, title, fullDescription, actionRequiredFullDescription } =
    BackupStatuses[backupStatus];

  const moreActions = actions[InventoryResourceActionsLevels.SubMore];
  const primaryActions = actions[InventoryResourceActionsLevels.SubPrimary];
  const secondaryActions = actions[InventoryResourceActionsLevels.SubSecondary];

  const shouldShowViolations =
    backupStatus === BackupStatus.ViolationsDetected ||
    backupStatus === BackupStatus.AllViolationsMuted;

  return (
    <StatusCardWrapper borderColor={iconColor}>
      <Stack marginLeft={'40px'} marginRight={'24px'}>
        <StatusCardHeader
          title={
            props.entity.backupStatus === BackupStatus.Dlsg
              ? 'Storage gateway'
              : 'Backup status'
          }
          optionMenuTestId='backup-status-card-options'
          actions={moreActions || []}
          entity={entity}
        />
      </Stack>
      <Stack alignItems={'center'} spacing={'12px'}>
        <BackupStatusIcon backupStatus={backupStatus} />
        <Typography
          data-testid='backup-status-title'
          variant='h3'
          sx={{ color: 'var(--mui-palette-text-primary) !important' }}
        >
          {title}
        </Typography>
        <Typography
          align={'center'}
          width={'65%'}
          className='text-textSecondary mb-[12px]'
          lineHeight={'20px'}
        >
          {actionRequiredFullDescription?.[entity.actionRequiredDetails!] ??
            fullDescription}
        </Typography>
        {shouldShowViolations && (
          <ViolationsDetails
            resourceId={entity.id}
            onEntityChange={onEntityChange}
            entity={entity}
          />
        )}
        <CardFooter
          primaryActions={primaryActions || []}
          secondaryActions={secondaryActions || []}
          entity={entity}
        />
      </Stack>
    </StatusCardWrapper>
  );
}

function CardFooter(props: {
  primaryActions: ResourceAction[];
  secondaryActions: ResourceAction[];
  entity: InventoryResource;
}) {
  const { primaryActions, secondaryActions, entity } = props;

  if (!primaryActions.length && !secondaryActions.length) {
    return null;
  }

  return (
    <Fragment>
      <Divider
        flexItem
        variant='middle'
        className='mx-auto mb-[12px] w-[70%]'
      />
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='center'
        width='100%'
      >
        {secondaryActions.map((action) => (
          <AuthorizationTooltip
            isAuthorized={action.isAuthorized}
            key={action.title}
          >
            <Button
              disabled={action.disabled?.()}
              onClick={() => action.execute(entity)}
              variant={'text'}
            >
              {action.icon && (
                <i
                  className={`${action.icon} text-[18px] mr-[5px] text-primary align-middle`}
                />
              )}
              {action.title ?? action.tooltip}
            </Button>
          </AuthorizationTooltip>
        ))}
        {!!secondaryActions.length && !!primaryActions.length && (
          <Divider
            orientation='vertical'
            flexItem
            variant='middle'
            className='mx-[14px] my-auto h-[24px]'
          />
        )}
        {primaryActions.map((action) => (
          <AuthorizationTooltip
            isAuthorized={action.isAuthorized}
            key={action.title}
          >
            <Button
              disabled={action.disabled?.()}
              onClick={() => action.execute(entity)}
              variant={'outlined'}
            >
              {action.icon && (
                <i
                  className={`${action.icon} text-[18px] mr-[5px] text-primary align-middle`}
                />
              )}
              {action.title ?? action.tooltip}
            </Button>
          </AuthorizationTooltip>
        ))}
      </Stack>
    </Fragment>
  );
}
